import { Button, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import useParentLoaderData from "../../../../../hooks/useParentLoaderData";
import NewResponseModal from "../../../../ui library/components/dialogs/NewResponseModal";
import HNSColors from "../../../../ui library/HNSColors";
import {
  applyConditionalRequiredRules,
  applyFieldTransformations,
  validateForm,
} from "../utils/functions";

import useRevalidateParent from "./../../../../../hooks/useRevalidateParent";
import DynamicField from "./components/DynamicField";
import { HistoryTooltip } from "./components/HistoryTooltip";

const DynamicForm = ({
  conditionalRules = {},
  data,
  extraButton = null,
  extraComponent = null,
  fieldTransformRules = {},
  formLayout,
  formRepository,
  historyData = {},
  lostReason = null,
}) => {
  const { _case } = useParentLoaderData(2);
  const formId = _case?.id;
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [formData, setFormData] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [options, setOptions] = useState({});
  const [showMoreButton, setShowMoreButton] = useState(false);
  const [errors, setErrors] = useState({});
  const [dynamicLayout, setDynamicLayout] = useState(formLayout);

  const isModified = useMemo(() => {
    return Object.keys(formData).some((key) => formData[key] !== initialValues[key]);
  }, [formData, initialValues]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const updatedLayout = formLayout.map((row) => ({
      ...row,
      row: row.row.map((field) => ({
        ...field,
        initialRequired: field.required ?? false,
      })),
    }));

    const layoutWithRequired = applyConditionalRequiredRules(
      null,
      updatedLayout,
      data,
      conditionalRules
    );

    const layoutWithTransforms = applyFieldTransformations(
      null,
      layoutWithRequired,
      data,
      fieldTransformRules
    );

    setDynamicLayout(layoutWithTransforms);
    setInitialValues(data);
    setFormData(data);
  }, [formLayout, data]);

  useEffect(() => {
    const validationErrors = validateForm({ dynamicLayout, formData });
    setErrors(validationErrors);
    setIsValidForm(Object.keys(validationErrors).length === 0);
  }, [dynamicLayout, formData]);

  async function handleSubmit(e) {
    e.preventDefault();

    const validationErrors = validateForm({ dynamicLayout, formData });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload = { ...formData };
    dynamicLayout.forEach((row) => {
      row.row.forEach((field) => {
        if (field.type === "date" && payload[field.name]) {
          const dateValue = new Date(payload[field.name]);
          if (!isNaN(dateValue)) {
            payload[field.name] = dateValue.toISOString().split("T")[0];
          }
        }
      });
    });

    // Remove unnecessary fields
    delete payload?.client;
    delete payload?.entity;
    delete payload?.updatedAt;
    delete payload?.updatedBy;
    delete payload?.createdAt;
    delete payload?.createdBy;
    delete payload?.relatedCases;

    try {
      await formRepository.patch(formId, payload);
      revalidateParent();
      setOpenSuccess(true);
    } catch (error) {
      setOpenError(true);
      setErrorMessage(error);
    }
  }

  return (
    <>
      <HistoryTooltip
        historyTab={{
          ...data,
          updatedByFallback: historyData?.updatedBy,
        }}
      />
      <form id="dynamic-fields-form" onSubmit={handleSubmit}>
        <div className="space-y-2">
          {dynamicLayout.map((group, rowIdx) => {
            const rowGroup = group.row.filter((field) => !field?.hidden);

            return (
              <Grid container spacing={2} key={rowIdx}>
                {rowGroup.map((field) => {
                  const fieldSize = 12 / rowGroup.length;
                  const error = Boolean(errors?.[field.name]);
                  const helperText = errors?.[field.name];
                  const historyfield = historyData?.[field.name];

                  return (
                    <Grid item xs={12} md={fieldSize} key={field.name}>
                      <DynamicField
                        conditionalRules={conditionalRules}
                        dynamicLayout={dynamicLayout}
                        error={error}
                        field={field}
                        fieldTransformRules={fieldTransformRules}
                        formData={formData}
                        helperText={helperText}
                        historyData={historyfield}
                        lostReason={lostReason}
                        options={options}
                        setDynamicLayout={setDynamicLayout}
                        setErrorMessage={setErrorMessage}
                        setErrors={setErrors}
                        setFormData={setFormData}
                        setOpenError={setOpenError}
                        setOptions={setOptions}
                        setShowMoreButton={setShowMoreButton}
                        showMoreButton={showMoreButton}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            );
          })}

          {extraComponent}

          <div className="flex w-full gap-2 pt-4">
            <Button
              sx={{
                background: HNSColors.secondary[500],
                "&:hover": { background: HNSColors.secondary[600] },
              }}
              disabled={!isModified || !isValidForm}
              className="h-[40px] flex-1 rounded-full py-[6px] text-sm normal-case transition"
              variant="contained"
              disableElevation
              type="submit"
            >
              Save
            </Button>

            <div>{extraButton}</div>
          </div>
        </div>
      </form>

      <NewResponseModal
        description="The information has been saved successfully"
        errorMessage={errorMessage}
        isError={openError}
        open={openSuccess || openError}
        setOpen={openError ? setOpenError : setOpenSuccess}
        title={openError ? "Error!" : "Success!"}
      />
    </>
  );
};

export default DynamicForm;
